/**
 * Compare Slice — Redux state for player comparison (2–4 players).
 *
 * State shape:
 *   { players: [{ _id, firstName, lastName }] }   max 4 entries
 *
 * Persisted in localStorage key "compare_players" so it survives page refreshes.
 */
import { createSlice } from '@reduxjs/toolkit';

const STORAGE_KEY = 'compare_players';
const MAX_COMPARE = 4;

const loadFromStorage = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.slice(0, MAX_COMPARE);
    }
  } catch {
    // ignore
  }
  return [];
};

const persist = players => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(players));
  } catch {
    // ignore
  }
};

export const compareSlice = createSlice({
  name: 'compare',
  initialState: {
    players: loadFromStorage(),
  },
  reducers: {
    /**
     * payload: { _id, firstName, lastName }
     */
    addToCompare: (state, action) => {
      const { _id } = action.payload;
      if (state.players.length >= MAX_COMPARE) return;
      if (state.players.some(p => p._id === _id)) return;
      state.players.push({
        _id: action.payload._id,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
      });
      persist(state.players);
    },
    removeFromCompare: (state, action) => {
      const id = action.payload;
      state.players = state.players.filter(p => p._id !== id);
      persist(state.players);
    },
    clearCompare: state => {
      state.players = [];
      persist([]);
    },
  },
});

export const { addToCompare, removeFromCompare, clearCompare } =
  compareSlice.actions;

/** Selector: just the IDs */
export const selectCompareIds = state => state.compare.players.map(p => p._id);

export const MAX_COMPARE_PLAYERS = MAX_COMPARE;

export default compareSlice.reducer;
